import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ClaimCodePanel from "@/components/ClaimCodePanel";
import { FileDown, Truck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export const SHIPMENT_STATUS_CLS = {
  requested: "bg-amber-100 text-amber-700",
  paid: "bg-blue-100 text-blue-700",
  label_generated: "bg-indigo-100 text-indigo-700",
  shipped: "bg-emerald-100 text-emerald-700",
};

/**
 * A single shipment card. The Sendcloud label + tracking are generated
 * AUTOMATICALLY once the client pays the shipping fee — there is no manual
 * "generate label" / "set tracking" UI anymore. This card only shows the
 * pre-shipment claim code, a one-click DOWNLOAD of the auto-generated label,
 * and the tracking display.
 */
export default function ShipmentRow({ s, onSaved, t }) {
  const [busy, setBusy] = useState(false);
  const hasLabel = !!(s.label_pdf_base64 || s.label_generated_at);

  async function downloadLabel() {
    setBusy(true);
    try {
      const res = await api(`/shipments/${s.id}/label`, { method: "POST" });
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${res.pdf_base64}`;
      link.download = `findloop-label-${s.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF");
      onSaved && onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-5" data-testid="shipment-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs text-slate-400 uppercase font-semibold">{t("shp.to")}</p>
          <p className="font-semibold text-slate-800">{s.customer_name}</p>
          <p className="text-sm text-slate-500">
            {s.address_line1}{s.address_line2 ? `, ${s.address_line2}` : ""} · {s.postal_code} {s.city} · {s.country}
          </p>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${SHIPMENT_STATUS_CLS[s.status] || "bg-slate-100 text-slate-600"}`}>{s.status}</span>
      </div>

      {s.status !== "shipped" && s.declaration_id && (
        <div className="mt-4 border-t border-border pt-4">
          <ClaimCodePanel declId={s.declaration_id} requirePhoto verificationQuestions={s.verification_questions} onChange={onSaved} />
        </div>
      )}

      <div className="mt-4 border-t border-border pt-4">
        {hasLabel ? (
          <Button onClick={downloadLabel} disabled={busy} className="rounded-xl font-semibold" data-testid="shipment-label-btn">
            <FileDown size={16} className="mr-1.5" /> {t("shp.download")}
          </Button>
        ) : (
          <p className="text-sm text-slate-500" data-testid="shipment-label-auto">
            {t("shp.labelAuto") === "shp.labelAuto"
              ? t("hc.theLabelIsGeneratedAutomatically")
              : t("shp.labelAuto")}
          </p>
        )}
        {s.tracking_number && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Truck size={15} className="text-findo-indigo shrink-0" />
            <span className="text-slate-500">{t("shp.tracking")} :</span>
            {s.tracking_url ? (
              <a href={s.tracking_url} target="_blank" rel="noreferrer" className="font-semibold text-findo-navy underline" data-testid="shipment-tracking-link">
                {s.tracking_number}
              </a>
            ) : (
              <span className="font-mono font-semibold text-findo-navy" data-testid="shipment-tracking-value">{s.tracking_number}</span>
            )}
            {s.carrier && <span className="text-slate-400">· {s.carrier}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
